import {
    BoxProps,
    Box,
    VStack,
    Button,
    Center,
    Spinner,
    Spacer,
    Tooltip,
    ButtonProps,
    ComponentWithAs,
} from '@chakra-ui/react';
import { useContext } from 'react';
import { Link } from 'react-router-dom';
import {
    FaInfoCircle,
    FaCubes,
    FaAngleDoubleLeft,
    FaAngleDoubleRight,
    FaCogs,
    FaCoins,
    FaGamepad,
    FaAward,
    FaList,
} from 'react-icons/fa';
import SelectedGameContext from '../context/SelectedGameContext';
import { useLocalStorage } from '../hooks/useLocalStorage';
import config from '../config';

enum SidebarState {
    SIDEBAR_MINIMIZED = 0,
    SIDEBAR_STANDARD = 1,
}

type SidebarButtonProps = ButtonProps & {
    isMinimized: boolean;
    name: string;
    icon: React.ReactElement;
};

const SidebarButton: ComponentWithAs<'button', SidebarButtonProps> = ({
    isMinimized,
    name,
    icon,
    ...rest
}) => {
    return (
        <Tooltip placement="right" label={isMinimized && name}>
            <Button
                leftIcon={isMinimized ? undefined : icon}
                justifyContent="flex-start"
                variant="ghost"
                width={isMinimized ? '' : '100%'}
                aria-label={name}
                {...rest}
            >
                {isMinimized ? <Center width={'100%'}>{icon}</Center> : name}
            </Button>
        </Tooltip>
    );
};

export const Sidebar: React.FC<BoxProps> = (props) => {
    const selectedGame = useContext(SelectedGameContext);
    const currLicense = selectedGame?.currLicense;
    const [sidebarState, setSidebarState] = useLocalStorage<SidebarState>(
        'sidebar_state',
        SidebarState.SIDEBAR_MINIMIZED,
    );

    const isMinimized = sidebarState == SidebarState.SIDEBAR_MINIMIZED;

    return (
        <Box
            as="nav"
            padding={5}
            width={isMinimized ? '50px' : '200px'}
            height="100%"
            backgroundColor="#898989"
            {...props}
        >
            <VStack height={'100%'}>
                {currLicense ? (
                    <>
                        <Box>
                            <SidebarButton
                                isMinimized={isMinimized}
                                name={'Overview'}
                                icon={<FaInfoCircle />}
                                as={Link}
                                to={`/game/${currLicense?.key}/overview`}
                            />
                            <SidebarButton
                                isMinimized={isMinimized}
                                name={'Unity / CSharp Actor'}
                                icon={<FaCubes />}
                                as={Link}
                                to={`/game/${currLicense?.key}/csharp-actor`}
                            />
                            <SidebarButton
                                isMinimized={isMinimized}
                                name={'Golang Actor'}
                                icon={<FaCubes />}
                                as={Link}
                                to={`/game/${currLicense?.key}/golang-actor`}
                            />

                            {config.features.leaderboards && (
                                <SidebarButton
                                    isMinimized={isMinimized}
                                    name={'Leaderboards'}
                                    icon={<FaAward />}
                                    as={Link}
                                    to={`/game/${currLicense?.key}/leaderboards`}
                                />
                            )}

                            {config.features.inventory && (
                                <SidebarButton
                                    isMinimized={isMinimized}
                                    name={'Inventory'}
                                    icon={<FaCoins />}
                                    as={Link}
                                    to={`/game/${currLicense?.key}/inventory`}
                                />
                            )}

                            <SidebarButton
                                isMinimized={isMinimized}
                                name={'Whitelist'}
                                icon={<FaList />}
                                as={Link}
                                to={`/game/${currLicense?.key}/whitelist`}
                            />

                            {config.features.runningGames && (
                                <SidebarButton
                                    isMinimized={isMinimized}
                                    name={'Running Games'}
                                    icon={<FaGamepad />}
                                    as={Link}
                                    to={`/game/${currLicense?.key}/sessions`}
                                />
                            )}

                            <SidebarButton
                                isMinimized={isMinimized}
                                name={'Settings'}
                                icon={<FaCogs />}
                                as={Link}
                                to={`/game/${currLicense?.key}/settings`}
                            />
                        </Box>
                        <Spacer />
                        <Box alignSelf={isMinimized ? undefined : 'flex-start'}>
                            <SidebarButton
                                isMinimized={isMinimized}
                                name={isMinimized ? 'Expand' : 'Collapse'}
                                icon={isMinimized ? <FaAngleDoubleRight /> : <FaAngleDoubleLeft />}
                                onClick={() =>
                                    setSidebarState((state) => {
                                        if (state == SidebarState.SIDEBAR_MINIMIZED) {
                                            return SidebarState.SIDEBAR_STANDARD;
                                        }
                                        return SidebarState.SIDEBAR_MINIMIZED;
                                    })
                                }
                            />
                        </Box>
                    </>
                ) : (
                    <Center width="100%" height="100%">
                        <Spinner data-cy="loading" />
                    </Center>
                )}
            </VStack>
        </Box>
    );
};
